import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Folder } from 'lucide-react';
import { attachmentsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';

interface CreateFolderModalProps {
  isOpen: boolean;
  projectId: string;
  parentId?: string;
  onClose: () => void;
  onSuccess?: (folder: any) => void;
}

export function CreateFolderModal({ isOpen, projectId, parentId, onClose, onSuccess }: CreateFolderModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (folderName: string) => attachmentsApi.createFolder(projectId, folderName, parentId),
    onSuccess: (folder) => {
      queryClient.invalidateQueries({ queryKey: ['project-folders', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
      setName('');
      setError(null);
      onClose();
      onSuccess?.(folder);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create folder');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError(null);
    createMutation.mutate(name.trim());
  };

  const handleClose = () => {
    setName('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent/10">
              <Folder className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-heading-4 text-secondary">New Folder</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6">
            <label htmlFor="folderName" className="block text-sm font-medium text-secondary mb-2">
              Folder Name <span className="text-error">*</span>
            </label>
            <input
              id="folderName"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="e.g., Design Assets, Documents"
              className="input"
              required
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-error">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Folder'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
